import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Tab,
  TabList,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogTitle,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';

// Inbound leads from the non-automated website booking flows (#G-02). These are
// emailed to staff AND persisted as a durable record (POST /bookings/
// front-loader-inquiry, /bookings/bulldog-bag-pickup → leads table). This page
// surfaces the persisted rows so a missed notification email no longer means a
// lost lead.

type LeadType = 'front_loader' | 'bulldog_bag';

interface Lead {
  id: string;
  lead_type: LeadType;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  details: Record<string, any> | null;
  has_site_photo: boolean;
  email_sent: boolean;
  email_id: string | null;
  source_ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}

interface ListResponse {
  data: Lead[];
}

const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  front_loader: 'Front-Loader',
  bulldog_bag: 'Bulldog Bag',
};

const useStyles = makeStyles({
  page: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('0', '16px'),
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  emptyState: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...shorthands.padding('16px', '24px', '8px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  detailBody: {
    ...shorthands.padding('16px', '24px', '24px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  detailNote: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.6,
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '12px',
  },
  errorBanner: {
    margin: '0 16px',
    padding: '12px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '4px',
    color: tokens.colorPaletteRedForeground1,
  },
});

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatBusinessDateTime(iso);
}

function fullName(lead: Lead): string {
  return `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || (lead.email || '—');
}

const TitleCase = (s: string) => s.slice(0, 1).toUpperCase() + s.slice(1);

const Leads: React.FC = () => {
  const styles = useStyles();
  const [filter, setFilter] = useState<'' | LeadType>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery<ListResponse>({
    queryKey: ['leads', filter],
    queryFn: async () => {
      const res = await apiFetch(`/leads${filter ? `?type=${filter}` : ''}`);
      if (!res.ok) throw new Error('Failed to load leads');
      return res.json();
    },
  });

  const leads = data?.data || [];
  const selected = leads.find((l) => l.id === selectedId) || null;

  const columns: ColumnConfig<Lead>[] = [
    {
      columnKey: 'created_at',
      label: 'Received',
      render: (l) => formatTimestamp(l.created_at),
    },
    {
      columnKey: 'lead_type',
      label: 'Type',
      render: (l) => (
        <Badge appearance="tint" color={l.lead_type === 'front_loader' ? 'brand' : 'informative'}>
          {LEAD_TYPE_LABELS[l.lead_type] || l.lead_type}
        </Badge>
      ),
    },
    {
      columnKey: 'first_name',
      label: 'Name',
      render: (l) => fullName(l),
    },
    {
      columnKey: 'email',
      label: 'Email',
      render: (l) => l.email || '—',
    },
    {
      columnKey: 'phone',
      label: 'Phone',
      render: (l) => l.phone || '—',
    },
    {
      columnKey: 'address',
      label: 'Location',
      render: (l) => l.address || '—',
    },
    {
      columnKey: 'email_sent',
      label: 'Notified',
      render: (l) => (
        <Badge appearance="tint" color={l.email_sent ? 'success' : 'danger'}>
          {l.email_sent ? 'Sent' : 'Not sent'}
        </Badge>
      ),
    },
  ];

  // Type-specific detail fields, derived from the JSON `details` blob.
  const renderDetailFields = (lead: Lead) => {
    const d = lead.details || {};
    if (lead.lead_type === 'front_loader') {
      const days: string[] = Array.isArray(d.pickup_days) ? d.pickup_days : [];
      return (
        <>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Dumpster</span>
            <span className={styles.detailValue}>
              {d.size ? `${String(d.size).toUpperCase()} front-loader` : '—'}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Contract</span>
            <span className={styles.detailValue}>
              Annual · preferred start {d.start_date || '—'}
              <br />
              Pickup days: {days.length ? days.map(TitleCase).join(', ') : '—'}
            </span>
          </div>
        </>
      );
    }
    // bulldog_bag
    return (
      <>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Requested Pickup Date</span>
          <span className={styles.detailValue}>{d.pickup_date || '—'}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Site Photo</span>
          <span className={styles.detailValue}>
            {lead.has_site_photo ? 'Submitted (emailed to staff — not stored)' : 'None'}
          </span>
        </div>
        {d.notes && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Instructions</span>
            <div className={styles.detailNote}>{d.notes}</div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbarRow}>
        <TabList
          selectedValue={filter}
          onTabSelect={(_, data) => setFilter(data.value as '' | LeadType)}
        >
          <Tab value="">All</Tab>
          <Tab value="front_loader">Front-Loader</Tab>
          <Tab value="bulldog_bag">Bulldog Bag</Tab>
        </TabList>
        <Button
          icon={<ArrowClockwise24Regular />}
          onClick={() => refetch()}
          disabled={isFetching}
          appearance="subtle"
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner}>{(error as Error).message}</div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && <div className={styles.emptyState}>Loading leads…</div>}

        {!isLoading && leads.length === 0 && !error && (
          <div className={styles.emptyState}>No leads yet.</div>
        )}

        {!isLoading && leads.length > 0 && (
          <DataTable
            data={leads}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="leads"
            itemsPerPage={50}
            showCheckboxes={false}
            primaryColumnKey="first_name"
            onRowClick={(l) => setSelectedId(l.id)}
          />
        )}
      </div>

      <Dialog
        open={!!selectedId}
        onOpenChange={(_, { open }) => { if (!open) setSelectedId(null); }}
      >
        <DialogSurface style={{ maxWidth: '90vw', width: '640px', padding: 0 }}>
          <DialogBody style={{ display: 'flex', flexDirection: 'column' }}>
            <div className={styles.detailHeader}>
              <DialogTitle style={{ margin: 0, fontSize: '18px' }}>
                {selected ? `${LEAD_TYPE_LABELS[selected.lead_type]} — ${fullName(selected)}` : 'Lead'}
              </DialogTitle>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={() => setSelectedId(null)}
              />
            </div>
            <DialogContent style={{ padding: 0 }}>
              {selected && (
                <div className={styles.detailBody}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Customer</span>
                    <span className={styles.detailValue}>
                      {fullName(selected)}
                      {selected.email && (
                        <>
                          {' '}&lt;
                          <a href={`mailto:${selected.email}`}>{selected.email}</a>
                          &gt;
                        </>
                      )}
                      {selected.phone && <><br />{selected.phone}</>}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Service Location</span>
                    <span className={styles.detailValue}>{selected.address || '—'}</span>
                  </div>

                  {renderDetailFields(selected)}

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Received</span>
                    <span className={styles.detailValue}>{formatTimestamp(selected.created_at)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Notification email</span>
                    <span className={styles.detailValue}>
                      {selected.email_sent
                        ? `Sent${selected.email_id ? ` (${selected.email_id})` : ''}`
                        : 'NOT sent — follow up manually'}
                    </span>
                  </div>
                  {(selected.source_ip || selected.user_agent) && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Origin</span>
                      <span className={`${styles.detailValue} ${styles.mono}`}>
                        {selected.source_ip || '—'}
                      </span>
                      {selected.user_agent && (
                        <span
                          className={`${styles.detailValue} ${styles.mono}`}
                          style={{ color: tokens.colorNeutralForeground3 }}
                        >
                          {selected.user_agent}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Leads;
